import { z } from 'zod';
import { EMPLOYMENT_TYPES, LEAVE_STATUSES } from '@bulk/types';
import { dateOnlySchema, idSchema, publicAssetSchema } from './common';
import { sessionSchema, trainerSchema, trainerSpecialtyTagSchema } from './classes';
import { compensationSchema } from './hr';

export const TRAINER_RESULTS_DAYS = 30;
export const TRAINER_UPCOMING_DAYS = 14;

export const trainerWeeklySlotSchema = z.object({
  scheduleId: idSchema,
  classId: idSchema,
  className: z.string(),
  classColor: z.string(),
  weekday: z.number().int().min(1).max(7).describe('ISO weekday: 1 Monday … 7 Sunday'),
  startTime: z.string().describe('HH:MM in the gym’s time zone'),
  durationMinutes: z.number().int(),
  locationName: z.string(),
});
export type TrainerWeeklySlotDto = z.infer<typeof trainerWeeklySlotSchema>;

const trainerProfileSchema = z.object({
  firstName: z.string(),
  lastName: z.string(),
  firstNameLocalized: z.record(z.string(), z.string()),
  lastNameLocalized: z.record(z.string(), z.string()),
  bio: z.string().nullable(),
  bioLocalized: z.record(z.string(), z.string()),
  photo: publicAssetSchema.nullable().describe('The website photo, falling back to the staff photo'),
  specialties: z.array(trainerSpecialtyTagSchema),
});

export const trainerCardSchema = trainerProfileSchema.extend({ id: idSchema });
export type TrainerCardDto = z.infer<typeof trainerCardSchema>;

export const trainerPageSchema = z.object({
  id: idSchema,
  active: z.boolean(),
  isSelf: z.boolean().describe('The signed-in person is this trainer'),
  profile: trainerProfileSchema,
  roster: trainerSchema.nullable().describe('The roster record with contact details and the website switch: trainers.read, or the trainer themself'),
  loginEmail: z.string().nullable().describe('The login linked to this trainer; same rule as roster'),
  upcoming: z.array(sessionSchema).describe(`Sessions this trainer teaches in the next ${TRAINER_UPCOMING_DAYS} days that the caller can see, with the member's own booking filled in`),
  weekly: z.array(trainerWeeklySlotSchema).describe('Active weekly slots this trainer teaches'),
  mine: z
    .object({ attended: z.number().int(), lastAttendedAt: z.string().nullable(), nextBookedAt: z.string().nullable() })
    .nullable()
    .describe("The signed-in member's classes with this trainer; null for anyone without a member profile"),
  results: z
    .object({
      days: z.number().int(),
      sessions: z.number().int(),
      capacity: z.number().int(),
      attended: z.number().int(),
      noShows: z.number().int(),
      unmarked: z.number().int(),
      members: z.number().int().describe('Different members who attended'),
      fillRate: z.number().nullable().describe('Attended over capacity, 0…1'),
    })
    .nullable()
    .describe(`Sessions held in the last ${TRAINER_RESULTS_DAYS} days: bookings.read, or the trainer themself`),
  employment: z
    .object({
      employeeId: idSchema,
      position: z.string().nullable(),
      departmentName: z.string().nullable(),
      employmentType: z.enum(EMPLOYMENT_TYPES),
      employmentStartDate: z.string(),
      compensation: compensationSchema.nullable().describe('Pay terms in force today; null without employees.pay.read, except for the trainer themself'),
    })
    .nullable()
    .describe('The employee record paid for this trainer: employees.read, or the trainer themself'),
  leave: z
    .array(z.object({ id: idSchema, leaveType: z.string(), leaveTypeLocalized: z.record(z.string(), z.string()), startDate: dateOnlySchema, endDate: dateOnlySchema, status: z.enum(LEAVE_STATUSES) }))
    .nullable()
    .describe('Leave from today on that is approved or waiting: leave.read, or the trainer themself'),
});
export type TrainerPageDto = z.infer<typeof trainerPageSchema>;
